import React, { useState, useEffect } from 'react'
import { createTask, updateTask, createSubtask, updateSubtask, deleteSubtask, getTasks } from '../utils/supabase'
import { useAuth } from '../hooks/useAuth'

// Convert UTC string to local datetime string for datetime-local input
const convertUTCToLocal = (utcString) => {
  if (!utcString) return '';
  
  const date = new Date(utcString);
  
  // Get local date components (JavaScript automatically handles timezone conversion)
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  // Return in datetime-local format
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

const TaskModal = ({ task, onSave, onClose }) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    deadline: '',
    priority: 3,
  })
  
  const [subtasks, setSubtasks] = useState([])

  useEffect(() => {
    if (task) {
      // Editing mode - convert UTC times to local for datetime-local inputs
      setFormData({
        title: task.title || '',
        description: task.description || '',
        deadline: convertUTCToLocal(task.deadline),
        priority: task.priority || 3,
      })
      
      // Convert subtask deadlines to local time too
      const localSubtasks = (task.subtasks || []).map(subtask => ({
        ...subtask,
        deadline: convertUTCToLocal(subtask.deadline)
      }))
      setSubtasks(localSubtasks)
    } else {
      // Creating mode
      setFormData({
        title: '',
        description: '',
        deadline: '',
        priority: 3,
      })
      setSubtasks([])
    }
  }, [task])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: name === 'priority' ? parseInt(value) : value
    }))
  }

  const handleSubtaskChange = (index, field, value) => {
    const newSubtasks = [...subtasks]
    newSubtasks[index] = {
      ...newSubtasks[index],
      [field]: value
    }
    setSubtasks(newSubtasks)
  }

  const addSubtask = () => {
    setSubtasks([...subtasks, { title: '', deadline: '', isNew: true }])
  }

  const removeSubtask = async (index) => {
    const subtask = subtasks[index]
    
    if (!subtask.isNew && subtask.id) {
      // Delete from database
      try {
        await deleteSubtask(subtask.id)
      } catch (error) {
        console.error('Error deleting subtask:', error)
        setError('Failed to delete subtask')
        return
      }
    }
    
    // Remove from local state
    const newSubtasks = subtasks.filter((_, i) => i !== index)
    setSubtasks(newSubtasks)
  }

  // Targeted XSS sanitization - allow international characters, remove dangerous patterns
  const sanitizeHTML = (str) => {
    if (!str) return '';
    return str
      // Remove script tags
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      // Remove javascript: URLs
      .replace(/javascript:/gi, '')
      // Remove event handlers (onclick, onload, etc.)
      .replace(/on\w+\s*=/gi, '')
      // Keep allowed characters: Unicode letters, Unicode numbers, and common symbols
      .replace(/[^\p{L}\p{N}~<>\\/'"`&@#$%^*()\-_+=[{}|:;,.?!\s\]]/gu, '');
  }

  const validateForm = () => {
    const title = formData.title.trim();
    const description = formData.description.trim();
    
    // Title validation
    if (!title) {
      setError('Title is required')
      return false
    }
    
    if (title.length < 1) {
      setError('Title must be at least 1 character')
      return false
    }
    
    if (title.length > 200) {
      setError('Title cannot exceed 200 characters')
      return false
    }
    
    // Description validation
    if (description.length > 1600) {
      setError('Description cannot exceed 1600 characters')
      return false
    }
    
    // Deadline validation
    if (!formData.deadline) {
      setError('Deadline is required')
      return false
    }
    
    const taskDeadline = new Date(formData.deadline)
    const now = new Date()
    
    // Check if deadline is in the past
    if (taskDeadline <= now) {
      setError('Deadline cannot be in the past')
      return false
    }
    
    // Subtask validation - check for incomplete subtasks
    const incompleteSubtasks = subtasks.filter(st => 
      (st.title.trim() && !st.deadline) || (!st.title.trim() && st.deadline)
    )
    
    if (incompleteSubtasks.length > 0) {
      setError('All subtasks must have both a title and deadline, or remove empty subtasks')
      return false
    }
    
    const validSubtasks = subtasks.filter(st => st.title.trim() && st.deadline)
    
    for (const subtask of validSubtasks) {
      const subtaskDeadline = new Date(subtask.deadline)
      
      // Check if subtask deadline is in the past
      // For new tasks, don't allow past dates
      // For existing tasks, allow past dates as long as they're after task creation
      if (subtaskDeadline <= now) {
        if (task) {
          // Editing existing task - allow past dates if after task creation
          const taskCreated = new Date(task.created_at)
          if (subtaskDeadline <= taskCreated) {
            setError('Subtask deadline cannot be before the task was created')
            return false
          }
        } else {
          // New task - don't allow past dates
          setError('Subtask deadline cannot be in the past')
          return false
        }
      }
      
      // Check if subtask is later than parent task
      if (subtaskDeadline > taskDeadline) {
        setError('Subtask deadline cannot be later than task deadline')
        return false
      }
      
      // Validate subtask title length
      if (subtask.title.trim().length > 200) {
        setError('Subtask title cannot exceed 200 characters')
        return false
      }
    }
    
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    
    if (!validateForm()) return
    
    setLoading(true)
    
    try {
      let savedTask
      
      if (task) {
        // Update existing task - convert local datetime to UTC and sanitize
        const { data, error } = await updateTask(task.id, {
          title: sanitizeHTML(formData.title.trim()),
          description: sanitizeHTML(formData.description.trim()),
          deadline: new Date(formData.deadline).toISOString(),
          priority: formData.priority,
        })
        
        if (error) throw error
        savedTask = data[0]
      } else {
        // Create new task - convert local datetime to UTC and sanitize
        const { data, error } = await createTask({
          title: sanitizeHTML(formData.title.trim()),
          description: sanitizeHTML(formData.description.trim()),
          deadline: new Date(formData.deadline).toISOString(),
          priority: formData.priority,
          creator_email: user.email,
          completed: false,
        })
        
        if (error) throw error
        savedTask = data[0]
      }
      
      // Handle subtasks - convert local datetime to UTC and sanitize
      const validSubtasks = subtasks.filter(st => st.title.trim() && st.deadline)
      
      for (const subtask of validSubtasks) {
        if (subtask.isNew) {
          // Create new subtask
          await createSubtask({
            task_id: savedTask.id,
            title: sanitizeHTML(subtask.title.trim()),
            deadline: new Date(subtask.deadline).toISOString(),
            done: false,
          })
        } else if (subtask.id) {
          // Update existing subtask
          await updateSubtask(subtask.id, {
            title: sanitizeHTML(subtask.title.trim()),
            deadline: new Date(subtask.deadline).toISOString(),
          })
        }
      }
      
      // Get the complete task with subtasks for optimistic update
      const { data: completeTask } = await getTasks()
      const fullTask = completeTask.find(t => t.id === savedTask.id)
      
      onSave(fullTask || savedTask)
    } catch (error) {
      console.error('Error saving task:', error)
      setError('Failed to save task: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal">
        <h2>{task ? 'Edit Task' : 'Create New Task'}</h2>
        
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="title">Title: ({formData.title.length}/200)</label>
            <input
              id="title"
              name="title"
              type="text"
              value={formData.title}
              onChange={handleInputChange}
              maxLength={200}
              required
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="description">Description: ({formData.description.length}/1600)</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Task description..."
              maxLength={1600}
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="deadline">Deadline:</label>
            <input
              id="deadline"
              name="deadline"
              type="datetime-local"
              value={formData.deadline}
              onChange={handleInputChange}
              required
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="priority">Priority (1-5):</label>
            <input
              id="priority"
              name="priority"
              type="number"
              min="1"
              max="5"
              value={formData.priority}
              onChange={handleInputChange}
              required
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label>Subtasks:</label>
            <div className="subtask-inputs">
              {subtasks.map((subtask, index) => (
                <div key={index} className="subtask-input">
                  <input
                    type="text"
                    placeholder="Subtask title"
                    value={subtask.title}
                    onChange={(e) => handleSubtaskChange(index, 'title', e.target.value)}
                    maxLength={200}
                    disabled={loading}
                  />
                  <input
                    type="datetime-local"
                    value={subtask.deadline}
                    onChange={(e) => handleSubtaskChange(index, 'deadline', e.target.value)}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => removeSubtask(index)}
                    disabled={loading}
                    className="remove-subtask-btn"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addSubtask}
              disabled={loading}
              className="btn btn-success"
            >
              Add Subtask
            </button>
          </div>
          
          <div className="modal-actions">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
            >
              {loading ? 'Saving...' : 'Save Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default TaskModal